import API from "Utils/api";
import { deepCamelCaseKeys } from "../../utils/collectionUtils";
import {
  OTAComponent,
  OTAComponentCreatePayload,
  OTAComponentUpdatePayload,
  OTAComponentVersion,
  OTAComponentVersionCreatePayload,
  OTARelease,
  VehicleType,
  OTAFleet,
  OTAVehicle,
  OTADeploymentSession,
  OTAShadow,
  DeploymentLogs,
} from "./types";

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

async function fetchAllPages<T>(
  url: string,
  params: Record<string, string> = {}
): Promise<T[]> {
  const results: T[] = [];
  let page = 1;
  let hasNext = true;
  while (hasNext) {
    const res = await API.get(url, { params: { ...params, page } });
    const data = res.data;
    const items: T[] = Array.isArray(data) ? data : data.results ?? [];
    results.push(...items);
    hasNext = Boolean(data.next);
    page++;
  }
  return results;
}

export async function deleteWithRetry(
  url: string,
  maxAttempts = 3,
  delayMs = 1000
): Promise<void> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      await API.delete(url);
      return;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 502 && attempt < maxAttempts) {
        await sleep(delayMs);
        continue;
      }
      throw err;
    }
  }
}

export const otaComponentsApi = {
  /**
   * Fetch all OTA components
   */
  getAll: async (): Promise<OTAComponent[]> => {
    const response = await API.get("ota/components/");
    const data = response.data.results || [];
    return deepCamelCaseKeys(data) as OTAComponent[];
  },

  /**
   * Fetch single OTA component by ID
   */
  getById: async (id: string): Promise<OTAComponent> => {
    const response = await API.get(`ota/components/${id}/`);
    return deepCamelCaseKeys(response.data) as OTAComponent;
  },

  /**
   * Create new OTA component (AWS Component Name auto-generated as com.cyngn.<name>)
   */
  create: async (payload: OTAComponentCreatePayload): Promise<OTAComponent> => {
    // Transform camelCase to snake_case for Django backend
    const backendPayload: Record<string, unknown> = {
      name: payload.name,
    };
    if (payload.type) backendPayload.type = payload.type;
    if (payload.description) backendPayload.description = payload.description;
    // Note: aws_component_name is auto-generated by backend, never sent from frontend

    const response = await API.post("ota/components/", backendPayload);
    return deepCamelCaseKeys(response.data) as OTAComponent;
  },

  /**
   * Update existing OTA component (AWS Component Name cannot be modified)
   */
  update: async (
    id: string,
    payload: OTAComponentUpdatePayload
  ): Promise<OTAComponent> => {
    // Transform camelCase to snake_case for Django backend
    const backendPayload: Record<string, unknown> = {};
    if (payload.name !== undefined) backendPayload.name = payload.name;
    if (payload.type !== undefined) backendPayload.type = payload.type;
    if (payload.description !== undefined)
      backendPayload.description = payload.description;
    // Note: aws_component_name is read-only and cannot be updated
    if (payload.isDeprecated !== undefined)
      backendPayload.is_deprecated = payload.isDeprecated;

    const response = await API.patch(`ota/components/${id}/`, backendPayload);
    return deepCamelCaseKeys(response.data) as OTAComponent;
  },

  /**
   * Delete OTA component
   */
  delete: async (id: string): Promise<void> => {
    await deleteWithRetry(`ota/components/${id}/`);
  },

  /**
   * Full bidirectional sync: pull new versions from GG into DB, then purge orphaned DB records.
   */
  syncGreengrass: async (): Promise<{
    status: "success" | "partial";
    vehicleTypesSynced: string[];
    pulledToDb: { component: string; version: string; arn: string }[];
    alreadySynced: { component: string; version: string }[];
    deletedTotal: number;
    purgeSummary: { component: string; deletedVersions: string[] }[];
    errors: { component: string; version?: string; error: string }[];
  }> => {
    const response = await API.post("ota/components/sync-greengrass/");
    return response.data;
  },

  /**
   * Pull component versions from GG into the DB.
   * Returns status "success" or "partial" (when there are type-conflict failures).
   */
  pullFromGreengrass: async (): Promise<{
    status: "success" | "partial";
    vehicle_types_synced: string[];
    pulled_to_db: { component: string; version: string; arn: string }[];
    already_synced: { component: string; version: string }[];
    failed: { component: string; version: string; error: string }[];
  }> => {
    const response = await API.post("ota/components/pull-from-greengrass/");
    return response.data;
  },

  /**
   * Fetch the allowed component types list
   */
  getTypes: async (): Promise<string[]> => {
    const response = await API.get("ota/components/types/");
    return response.data.types as string[];
  },

  /**
   * Toggle component deprecation status
   */
  toggleDeprecation: async (id: string): Promise<OTAComponent> => {
    const response = await API.post(`ota/components/${id}/deprecate/`);
    return deepCamelCaseKeys(
      response.data.data || response.data
    ) as OTAComponent;
  },

  /**
   * Bulk-import Container components from a name→tag map.
   * Creates missing components and a new version for each entry.
   */
  bulkImport: async (
    components: Record<string, string>
  ): Promise<{
    created: {
      name: string;
      componentCreated: boolean;
      version: string;
      arn: string;
    }[];
    errors: { name: string; error: string }[];
  }> => {
    const response = await API.post("ota/components/bulk-import/", {
      components,
    });
    return deepCamelCaseKeys(response.data) as {
      created: {
        name: string;
        componentCreated: boolean;
        version: string;
        arn: string;
      }[];
      errors: { name: string; error: string }[];
    };
  },
};

export const otaVersionsApi = {
  /**
   * Fetch all versions for a component
   */
  getByComponent: async (
    componentId: string
  ): Promise<OTAComponentVersion[]> => {
    const response = await API.get(`ota/versions/?component_id=${componentId}`);
    const data = response.data.results || [];
    return deepCamelCaseKeys(data) as OTAComponentVersion[];
  },

  /**
   * Fetch single version by ID
   */
  getById: async (id: string): Promise<OTAComponentVersion> => {
    const response = await API.get(`ota/versions/${id}/`);
    return deepCamelCaseKeys(response.data) as OTAComponentVersion;
  },

  /**
   * Create new component version with manual SHA-256 hash
   */
  create: async (
    payload: OTAComponentVersionCreatePayload
  ): Promise<OTAComponentVersion> => {
    // Transform camelCase to snake_case for Django backend
    const backendPayload: Record<string, unknown> = {
      component_id: payload.componentId,
      version_number: payload.versionNumber,
    };

    // Firmware fields
    if (payload.s3Uri !== undefined) backendPayload.s3_uri = payload.s3Uri;
    if (payload.sha256Hash !== undefined)
      backendPayload.sha256_hash = payload.sha256Hash;
    if (payload.firmwareVersion !== undefined)
      backendPayload.firmware_version = payload.firmwareVersion;

    // Container fields
    if (payload.ccmImageTag !== undefined)
      backendPayload.ccm_image_tag = payload.ccmImageTag;

    // Host fields
    if (payload.hostPackageVersion !== undefined)
      backendPayload.host_package_version = payload.hostPackageVersion;

    // Optional metadata
    if (payload.description !== undefined)
      backendPayload.description = payload.description;
    if (payload.tags !== undefined) backendPayload.tags = payload.tags;

    const response = await API.post("ota/versions/", backendPayload);
    return deepCamelCaseKeys(response.data) as OTAComponentVersion;
  },

  /**
   * Update description and/or tags on an existing version.
   */
  update: async (
    id: string,
    payload: { description?: string | null; tags?: string[] }
  ): Promise<OTAComponentVersion> => {
    const response = await API.patch(`ota/versions/${id}/`, payload);
    return deepCamelCaseKeys(response.data) as OTAComponentVersion;
  },

  /**
   * Upload a run.yaml file and create container component versions for every service entry.
   * Parses lock-tag → ccm_service_name + ccm_image_tag, source → ccm_source_name,
   * and remaining lock fields → ccm_lock_extra.
   */
  importRunYaml: async (
    file: File
  ): Promise<{
    created: {
      name: string;
      componentCreated: boolean;
      version: string;
      imageTag: string;
      arn: string;
    }[];
    errors: { name: string; error: string }[];
  }> => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await API.post("ota/versions/import-run-yaml/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return deepCamelCaseKeys(response.data) as {
      created: {
        name: string;
        componentCreated: boolean;
        version: string;
        imageTag: string;
        arn: string;
      }[];
      errors: { name: string; error: string }[];
    };
  },

  /**
   * Delete component version (removes from Greengrass and database)
   */
  delete: async (id: string): Promise<void> => {
    await deleteWithRetry(`ota/versions/${id}/`);
  },

  /**
   * Fetch SHA-256 hash of the file stored at the given S3 URI.
   * Returns the hex digest string.
   */
  fetchSha256: async (s3Uri: string): Promise<string> => {
    const response = await API.post("ota/versions/fetch-sha256/", {
      s3_uri: s3Uri,
    });
    return (response.data as { sha256Hash: string }).sha256Hash;
  },

  /**
   * Toggle version deprecation status
   */
  toggleDeprecation: async (id: string): Promise<OTAComponentVersion> => {
    const response = await API.post(`ota/versions/${id}/deprecate/`);
    return deepCamelCaseKeys(
      response.data.data || response.data
    ) as OTAComponentVersion;
  },
};

export const vehicleTypesApi = {
  /**
   * Fetch all vehicle types
   */
  getAll: async (): Promise<VehicleType[]> => {
    // Use OTA vehicle types endpoint (OTA registry)
    const response = await API.get("ota/ota-vehicle-types/");
    const data = response.data.results || response.data || [];
    return deepCamelCaseKeys(data) as VehicleType[];
  },
};

export const otaFleetsApi = {
  /**
   * Fetch all OTA fleets
   */
  getAll: async (): Promise<OTAFleet[]> => {
    const data = await fetchAllPages<unknown>("ota/fleets/");
    return deepCamelCaseKeys(data) as OTAFleet[];
  },

  /**
   * Fetch single OTA fleet by ID
   */
  getById: async (id: string): Promise<OTAFleet> => {
    const response = await API.get(`ota/fleets/${id}/`);
    return deepCamelCaseKeys(response.data) as OTAFleet;
  },

  /**
   * Create a new fleet
   */
  create: async (payload: {
    name: string;
    description?: string;
  }): Promise<OTAFleet> => {
    const response = await API.post("ota/fleets/", payload);
    return deepCamelCaseKeys(response.data) as OTAFleet;
  },

  /**
   * Update a fleet
   */
  update: async (
    id: string,
    payload: { name?: string; description?: string; isActive?: boolean }
  ): Promise<OTAFleet> => {
    const requestPayload: Record<string, unknown> = {};
    if (payload.name !== undefined) requestPayload.name = payload.name;
    if (payload.description !== undefined)
      requestPayload.description = payload.description;
    if (payload.isActive !== undefined)
      requestPayload.is_active = payload.isActive;

    const response = await API.patch(`ota/fleets/${id}/`, requestPayload);
    return deepCamelCaseKeys(response.data) as OTAFleet;
  },

  /**
   * Delete a fleet (only if no vehicles assigned)
   */
  delete: async (id: string): Promise<void> => {
    await deleteWithRetry(`ota/fleets/${id}/`);
  },

  /**
   * Detach all vehicles from a fleet (sets fleet = null on each vehicle)
   */
  detachVehicles: async (id: string): Promise<{ detached: number }> => {
    const response = await API.post(`ota/fleets/${id}/detach-vehicles/`);
    return response.data;
  },

  /**
   * Delete all deployment sessions for a fleet (also cancels on AWS)
   */
  deleteSessions: async (id: string): Promise<{ deleted: number }> => {
    const response = await API.post(`ota/fleets/${id}/delete-sessions/`);
    return response.data;
  },

  /**
   * Get vehicles for a fleet
   */
  getVehicles: async (fleetId: string): Promise<OTAVehicle[]> => {
    const response = await API.get(`ota/vehicles/?fleet_id=${fleetId}`);
    const data = response.data.results || response.data || [];
    return deepCamelCaseKeys(data) as OTAVehicle[];
  },

  /**
   * Sync OTAFleet rows against AWS IoT Thing Groups and link to legacy FMS fleets.
   */
  syncGreengrass: async (): Promise<{
    updated: number;
    created: number;
    linkedToLegacy: number;
    provisionedToGg: number;
    errors: string[];
  }> => {
    const response = await API.post("ota/fleets/sync-greengrass/");
    return response.data;
  },
};

export const otaVehiclesApi = {
  /**
   * Fetch all OTA vehicles
   */
  getAll: async (): Promise<OTAVehicle[]> => {
    const data = await fetchAllPages<unknown>("ota/vehicles/");
    return deepCamelCaseKeys(data) as OTAVehicle[];
  },

  /**
   * Fetch single OTA vehicle by ID
   */
  getById: async (id: string): Promise<OTAVehicle> => {
    const response = await API.get(`ota/vehicles/${id}/`);
    return deepCamelCaseKeys(response.data) as OTAVehicle;
  },

  getLogs: async (
    id: string,
    params?: { hours?: number; components?: string[] }
  ): Promise<DeploymentLogs> => {
    const q: Record<string, string | number> = {};
    if (params?.hours != null) q.hours = params.hours;
    if (params?.components?.length) q.components = params.components.join(",");
    const response = await API.get(`ota/vehicles/${id}/logs/`, { params: q });
    return response.data as DeploymentLogs;
  },

  getShadow: async (id: string): Promise<OTAShadow | null> => {
    const response = await API.get(`ota/vehicles/${id}/shadow/`);
    return response.data.shadow ?? null;
  },

  getThingAttributes: async (id: string): Promise<Record<string, string>> => {
    const response = await API.get(`ota/vehicles/${id}/thing-attributes/`);
    return response.data.attributes ?? {};
  },

  /**
   * Update a vehicle
   */
  update: async (
    id: string,
    payload: { fleetId?: string; notes?: string; isActive?: boolean }
  ): Promise<OTAVehicle> => {
    const requestPayload: Record<string, unknown> = {};
    if (payload.fleetId !== undefined)
      requestPayload.fleet_id = payload.fleetId;
    if (payload.notes !== undefined) requestPayload.notes = payload.notes;
    if (payload.isActive !== undefined)
      requestPayload.is_active = payload.isActive;

    const response = await API.patch(`ota/vehicles/${id}/`, requestPayload);
    return deepCamelCaseKeys(response.data) as OTAVehicle;
  },

  /**
   * Delete a vehicle (and cleanup AWS resources)
   */
  delete: async (id: string): Promise<void> => {
    await deleteWithRetry(`ota/vehicles/${id}/`);
  },

  /**
   * Get components linked to a vehicle (via vehicle type inheritance)
   *
   * INHERITANCE LOGIC:
   * 1. A Vehicle does NOT have a direct link to components
   * 2. A Vehicle has a Foreign Key to OTAVehicleType (vehicle_type_id)
   * 3. OTAVehicleType is linked to OTAComponents via ota_vehicle_components_link
   * 4. Therefore: Vehicle "inherits" the component list from its assigned type
   *
   * Implementation:
   * - Backend provides /api/v1/ota/vehicles/<id>/components/ endpoint
   * - This endpoint handles the inheritance logic server-side
   * - Returns components linked to the vehicle's type (empty array if no type assigned)
   */
  getLinkedComponents: async (vehicleId: string): Promise<OTAComponent[]> => {
    const response = await API.get(`ota/vehicles/${vehicleId}/components/`);
    return deepCamelCaseKeys(response.data) as OTAComponent[];
  },

  /**
   * Trigger an immediate Greengrass core device sync.
   * Auto-registers new devices and marks missing ones as deprecated.
   * Returns 202 Accepted — the sync runs asynchronously in the background.
   */
  syncGreengrass: async (): Promise<{
    updated: number;
    created: number;
    linkedToLegacy: number;
    enriched: number;
    errors: string[];
  }> => {
    const response = await API.post("ota/vehicles/sync-greengrass/");
    return response.data;
  },
};

export const deploymentSessionsApi = {
  getAll: async (params?: {
    fleetId?: string;
    status?: string;
  }): Promise<OTADeploymentSession[]> => {
    const queryParams: Record<string, string> = {};
    if (params?.fleetId) queryParams.fleet_id = params.fleetId;
    if (params?.status) queryParams.status = params.status;
    const data = await fetchAllPages<unknown>(
      "ota/deployment-sessions/",
      queryParams
    );
    return deepCamelCaseKeys(data) as OTADeploymentSession[];
  },

  getById: async (id: string): Promise<OTADeploymentSession> => {
    const response = await API.get(`ota/deployment-sessions/${id}/`);
    return deepCamelCaseKeys(response.data) as OTADeploymentSession;
  },

  delete: async (id: string): Promise<void> => {
    await deleteWithRetry(`ota/deployment-sessions/${id}/`);
  },

  getLogs: async (
    id: string,
    components?: string[]
  ): Promise<DeploymentLogs> => {
    const params: Record<string, string> = {};
    if (components && components.length > 0) {
      params.components = components.join(",");
    }
    const response = await API.get(`ota/deployment-sessions/${id}/logs/`, {
      params,
    });
    return response.data as DeploymentLogs;
  },
};

export interface OTALogLine {
  ts: string;
  level: string;
  phase: string;
  component: string;
  session_id: string;
  vehicle_id: string;
  job: string;
  msg: string;
}

export interface OTALogsResult {
  lines: OTALogLine[];
  total: number;
  truncated: boolean;
}

export interface BackendLogLine {
  ts: string;
  msg: string;
  level: string;
  service: string;
  component: string;
  env: string;
  [key: string]: string;
}

export interface BackendLogsResult {
  lines: BackendLogLine[];
  total: number;
  truncated: boolean;
}

export const backendLogsApi = {
  query: async (params: {
    component?: string;
    operation?: string;
    level?: string;
    env?: string;
    start?: number;
    end?: number;
    limit?: number;
  }): Promise<BackendLogsResult> => {
    const q: Record<string, string | number> = {};
    if (params.component) q.component = params.component;
    if (params.operation) q.operation = params.operation;
    if (params.level) q.level = params.level;
    if (params.env) q.env = params.env;
    if (params.start != null) q.start = params.start;
    if (params.end != null) q.end = params.end;
    if (params.limit != null) q.limit = params.limit;
    const response = await API.get("ota/backend-logs/", { params: q });
    return response.data as BackendLogsResult;
  },
};

export const otaSystemApi = {
  getBackendVersion: async (): Promise<string> => {
    const response = await API.get("ota/system-version/");
    return response.data.version as string;
  },
};

export const releasesApi = {
  /**
   * Create a full OTA release by uploading a YAML/JSON manifest file.
   * POST /ota/releases/import-release/
   */
  importRelease: async (file: File): Promise<OTARelease> => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await API.post("ota/releases/import-release/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return deepCamelCaseKeys(response.data) as OTARelease;
  },

  /**
   * Create component versions from a manifest file without creating a release.
   * POST /ota/releases/import-versions/
   */
  importVersionsFromManifest: async (
    file: File
  ): Promise<{
    summary: {
      totalComponents: number;
      created: { name: string; versionNumber: string; type: string }[];
      reused: { name: string; versionNumber: string; type: string }[];
    };
  }> => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await API.post("ota/releases/import-versions/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return deepCamelCaseKeys(response.data) as {
      summary: {
        totalComponents: number;
        created: { name: string; versionNumber: string; type: string }[];
        reused: { name: string; versionNumber: string; type: string }[];
      };
    };
  },
};

export const otaLogsApi = {
  query: async (params: {
    sessionId?: string;
    vehicleId?: string;
    job?: string;
    phase?: string;
    level?: string;
    start?: number;
    end?: number;
    limit?: number;
  }): Promise<OTALogsResult> => {
    const q: Record<string, string | number> = {};
    if (params.sessionId) q.session_id = params.sessionId;
    if (params.vehicleId) q.vehicle_id = params.vehicleId;
    if (params.job) q.job = params.job;
    if (params.phase) q.phase = params.phase;
    if (params.level) q.level = params.level;
    if (params.start != null) q.start = params.start;
    if (params.end != null) q.end = params.end;
    if (params.limit != null) q.limit = params.limit;
    const response = await API.get("ota/logs/", { params: q });
    return response.data as OTALogsResult;
  },
};
